import { useState, useRef, useEffect } from "react";
import { FiHelpCircle, FiShield } from "react-icons/fi";
import { MfaIconBadge, backupCodeInputClass, otpInputClass } from "./MfaUi";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import RecoveryModal from "./RecoveryModal";
import { useMfaLogin } from "./useMfa";
import { useAuth } from "context/auth-context";
import { toastrError, toastrSuccess } from "lib/toastr";

type LocationState = {
  remember?: boolean;
  signInData?: any;
};

const MfaVerifyPage = () => {
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const [backupCode, setBackupCode] = useState<string>("");
  const [useBackup, setUseBackup] = useState<boolean>(false);
  const [showHelp, setShowHelp] = useState<boolean>(false);

  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const { setAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const { mutateAsync: verifyMfaLogin, isLoading: loading } = useMfaLogin();

  const { remember, signInData } = (location.state || {}) as LocationState;
  // const { remember, email } = (location.state || {}) as any;

  useEffect(() => {
    if (!signInData) {
      window.location.href = "/log-in";
    }
  }, [signInData]);

  // 🔹 OTP input
  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "");

    if (paste.length === 6) {
      setOtp(paste.split(""));
      inputsRef.current[5]?.focus();
    }
  };

  // 🔥 Verify
  const handleVerify = async () => {
    const finalOtp = otp.join("");

    if (!useBackup && finalOtp.length !== 6) {
      return toast.error("Enter valid 6-digit OTP");
    }

    if (useBackup && !backupCode) {
      return toast.error("Enter backup code");
    }

    try {
      const payload = {
        email: signInData?.data?.email,
        otp: useBackup ? undefined : finalOtp,
        backupCode: useBackup ? backupCode.trim() : undefined,
      };

      const res = await verifyMfaLogin(payload);

      const response = res?.data;

      if (response?.success) {
        // ✅ SAVE TOKEN
        localStorage.setItem("access_token", response.token);
        // ✅ SET AUTH (same as login)
        setAuth(response.result);
        // ✅ optional cookie
        Cookies.set("userData", JSON.stringify(response.result), {
          expires: remember ? 30 : undefined,
        });
        toastrSuccess(response.message || "Login successful");
        navigate("/dashboard", { replace: true });
      } else {
        toastrError(response?.message || "Verification failed");
        navigate("/login", { replace: true });
      }
    } catch (err: any) {
      toastrError(err?.response?.data?.message || "Invalid code");
    }
  };

  return (
    <div className="flex items-center justify-center w-full min-h-[calc(100vh-var(--zs-public-header-h,64px))] px-4 py-10 sm:py-16">
      <div className="w-full max-w-md">
        <div className="p-6 zs-card sm:p-8">
          {/* Header */}
          <div className="flex flex-col items-center mb-6 text-center">
            <div className="mb-4">
              <MfaIconBadge size="lg">
                <FiShield />
              </MfaIconBadge>
            </div>

            <span className="zs-eyebrow">Two-step verification</span>
            <h2 className="text-xl font-semibold font-display text-ink">Multi-Factor Authentication</h2>

            <p className="mt-1.5 text-sm text-ink-3">
              {useBackup
                ? "Enter one of your backup codes"
                : "Enter 6-digit code from your authenticator app"}
            </p>
          </div>

          {/* OTP */}
          {!useBackup ? (
            <div className="flex justify-center w-full gap-2 mx-auto mb-6">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  value={digit}
                  inputMode="numeric"
                  aria-label={`Digit ${index + 1}`}
                  ref={(el) => (inputsRef.current[index] = el)}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  className={otpInputClass}
                />
              ))}
            </div>
          ) : (
            <input
              type="text"
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
              placeholder="Enter backup code"
              className={`${backupCodeInputClass} mb-6`}
            />
          )}

          {/* Button */}
          <button onClick={handleVerify} disabled={loading} className="zs-btn zs-btn-primary zs-btn-lg zs-btn-block">
            {loading ? "Verifying..." : "Verify & Continue"}
          </button>

          {/* Toggle */}
          <div className="flex flex-col items-center pt-5 mt-6 text-center border-t border-line">
            <p className="text-sm text-ink-3">Don&apos;t have access to your authenticator app?</p>

            <div className="flex flex-wrap items-center justify-center mt-2 gap-x-3 gap-y-1">
              <button
                onClick={() => {
                  setUseBackup(!useBackup);
                  setOtp(new Array(6).fill(""));
                  setBackupCode("");
                }}
                className="text-sm zs-link"
              >
                {useBackup ? "Use Authenticator Code" : "Use Backup Code"}
              </button>

              <p className="text-xs text-ink-4">or</p>

              <button
                onClick={() => setShowHelp(true)}
                className="flex items-center justify-center gap-1 text-sm zs-link"
              >
                <FiHelpCircle />
                Recover your account
              </button>
            </div>
          </div>

          {showHelp && <RecoveryModal onClose={() => setShowHelp(false)} />}
        </div>

        <p className="mt-4 text-xs text-center text-ink-4">Each backup code can be used only once</p>
      </div>
    </div>
  );
};

export default MfaVerifyPage;
